import React from 'react';
import { useBrandingColors } from '../../hooks/useBrandingColors';
import type { TestMessages, InterviewMessage } from '../../../generated-src/client/interview-settings/api';
import { apiService } from '../../services/apiService';

// Глобальное хранилище для TestMessageSection
const testGlobalTexts: { [key: string]: string } = {};

// Компонент для префикс‑сообщения (инструкция перед тестом)
interface PreMessageInputProps {
  initialText: string;
}

const PreMessageInput: React.FC<PreMessageInputProps> = React.memo(({ initialText }) => {
  const [text, setText] = React.useState(() => {
    return testGlobalTexts['test-pre-message'] || initialText;
  });

  const adjustTextareaHeight = (textarea: HTMLTextAreaElement) => {
    textarea.style.height = 'auto';
    textarea.style.height = Math.max(textarea.scrollHeight, 40) + 'px';
  };

  const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const newText = e.target.value;
    setText(newText);
    testGlobalTexts['test-pre-message'] = newText;
    adjustTextareaHeight(e.target);
  };

  React.useEffect(() => {
    const textarea = document.querySelector(`textarea[data-id="test-pre-message"]`) as HTMLTextAreaElement;
    if (textarea && text) {
      adjustTextareaHeight(textarea);
    }
  }, [text]);

  return (
    <div className="p-1">
      <label className="block text-sm font-medium text-[#6b7280] mb-2">
        Инструкция перед тестовым сообщением
      </label>
      <textarea
        data-id="test-pre-message"
        value={text}
        onChange={handleChange}
        placeholder="Введите инструкцию перед тестовым сообщением..."
        className="w-full px-3 py-2 border border-[#cdd0d5] rounded-xl focus:outline-none focus:ring-2 focus:ring-[#e16349] focus:border-transparent resize-none overflow-hidden text-sm text-[#525866]"
        rows={1}
        style={{ minHeight: '44px' }}
      />
    </div>
  );
});

// Компонент для главного тестового сообщения
interface TestMessageInputProps {
  initialText: string;
}

const TestMessageInput: React.FC<TestMessageInputProps> = React.memo(({ initialText }) => {
  const [text, setText] = React.useState(() => {
    return testGlobalTexts['test-message'] || initialText;
  });

  const adjustTextareaHeight = (textarea: HTMLTextAreaElement) => {
    textarea.style.height = 'auto';
    textarea.style.height = Math.max(textarea.scrollHeight, 40) + 'px';
  };

  const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const newText = e.target.value;
    setText(newText);
    testGlobalTexts['test-message'] = newText;
    adjustTextareaHeight(e.target);
  };

  React.useEffect(() => {
    const textarea = document.querySelector(`textarea[data-id="test-message"]`) as HTMLTextAreaElement;
    if (textarea && text) {
      adjustTextareaHeight(textarea);
    }
  }, [text]);

  return (
    <div className="p-1">
      <label className="block text-sm font-medium text-[#525866] mb-2">
        Тестовое сообщение
      </label>
      <textarea
        data-id="test-message"
        value={text}
        onChange={handleChange}
        placeholder="Введите тестовое сообщение для микрофона..."
        className="w-full px-3 py-2 border border-[#cdd0d5] rounded-xl focus:outline-none focus:ring-2 focus:ring-[#e16349] focus:border-transparent resize-none overflow-hidden text-sm"
        rows={1}
        style={{ minHeight: '44px' }}
      />
    </div>
  );
});

// Компонент для позитивных/негативных ответов
interface ResponseInputProps {
  id: string;
  index: number;
  initialText: string;
  type: 'positive' | 'negative';
  onRemove: (index: number) => void;
}

const ResponseInput: React.FC<ResponseInputProps> = React.memo(({ 
  id, 
  index, 
  initialText, 
  type,
  onRemove
}) => {
  const colors = useBrandingColors();
  const [text, setText] = React.useState(() => {
    return testGlobalTexts[id] || initialText;
  });

  const adjustTextareaHeight = (textarea: HTMLTextAreaElement) => {
    textarea.style.height = 'auto';
    textarea.style.height = Math.max(textarea.scrollHeight, 40) + 'px';
  };

  const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const newText = e.target.value;
    setText(newText);
    testGlobalTexts[id] = newText;
    adjustTextareaHeight(e.target);
  };

  React.useEffect(() => {
    const textarea = document.querySelector(`textarea[data-id="${id}"]`) as HTMLTextAreaElement;
    if (textarea && text) {
      adjustTextareaHeight(textarea);
    }
  }, [id, text]);

  const typeName = type === 'positive' ? 'Позитивный' : 'Негативный';

  return (
    <div className="flex items-start gap-3 p-1">
      <div className="flex-1">
        <div className="flex justify-between items-center mb-2">
          <label className="block text-sm font-medium text-[#525866]">
            {typeName} ответ {index + 1}
          </label>
          <button
            onClick={() => onRemove(index)}
            className="text-sm font-medium transition-colors"
            style={{ color: colors.primary }}
            onMouseEnter={(e) => { e.currentTarget.style.color = colors.primaryHover; }}
            onMouseLeave={(e) => { e.currentTarget.style.color = colors.primary; }}
          >
            Удалить
          </button>
        </div>
        <textarea
          data-id={id}
          value={text}
          onChange={handleChange}
          placeholder={`Введите ${typeName.toLowerCase()} ответ ${index + 1}...`}
          className="w-full px-3 py-2 border border-[#cdd0d5] rounded-xl focus:outline-none focus:ring-2 focus:ring-[#e16349] focus:border-transparent resize-none overflow-hidden text-sm"
          rows={1}
          style={{ minHeight: '44px' }}
        />
      </div>
    </div>
  );
});

interface Props {
  embedded?: boolean;
}

export const TestMessageSection: React.FC<Props> = ({ embedded = false }) => {
  const [preText, setPreText] = React.useState('');
  const [testText, setTestText] = React.useState('');
  const [positiveResponses, setPositiveResponses] = React.useState<Array<{ id: string; text: string }>>([]);
  const [negativeResponses, setNegativeResponses] = React.useState<Array<{ id: string; text: string }>>([]);
  const [error, setError] = React.useState('');
  const [saving, setSaving] = React.useState(false);
  const colors = useBrandingColors();

  React.useEffect(() => {
    loadTest();
  }, []);

  const loadTest = async () => {
    try {
      const response: TestMessages = await apiService.getTestMessages();
      const preMessageText = response.testPreMessage?.text || '';
      const testMessageText = response.testMessage?.text || '';
      const positive = (response.testPositiveResponses || []).map((r: InterviewMessage) => r.text || '');
      const negative = (response.testNegativeResponses || []).map((r: InterviewMessage) => r.text || '');
      
      setPreText(preMessageText);
      setTestText(testMessageText);
      
      // Конвертируем в массивы с ID
      const positiveItems = positive.map((text: string, i: number) => ({
        id: `positive-${i}-${Date.now()}`,
        text
      }));
      const negativeItems = negative.map((text: string, i: number) => ({
        id: `negative-${i}-${Date.now()}`,
        text
      }));
      
      setPositiveResponses(positiveItems.length > 0 ? positiveItems : [{ id: `positive-0-${Date.now()}`, text: '' }]);
      setNegativeResponses(negativeItems.length > 0 ? negativeItems : [{ id: `negative-0-${Date.now()}`, text: '' }]);
    } catch (error) {
      setError('Ошибка при загрузке теста');
    }
  };

  const handleAddPositive = React.useCallback(() => {
    const newId = `positive-${positiveResponses.length}-${Date.now()}`;
    setPositiveResponses(prev => [...prev, { id: newId, text: '' }]);
  }, [positiveResponses.length]);

  const handleRemovePositive = React.useCallback((index: number) => {
    setPositiveResponses(prev => {
      if (prev.length <= 1) return prev;
      const item = prev[index];
      if (item) {
        delete testGlobalTexts[item.id];
      }
      return prev.filter((_, i) => i !== index);
    });
  }, []);

  const handleAddNegative = React.useCallback(() => {
    const newId = `negative-${negativeResponses.length}-${Date.now()}`;
    setNegativeResponses(prev => [...prev, { id: newId, text: '' }]);
  }, [negativeResponses.length]);

  const handleRemoveNegative = React.useCallback((index: number) => {
    setNegativeResponses(prev => {
      if (prev.length <= 1) return prev;
      const item = prev[index];
      if (item) {
        delete testGlobalTexts[item.id];
      }
      return prev.filter((_, i) => i !== index);
    });
  }, []);

  const handleSave = React.useCallback(async () => {
    try {
      setError('');
      setSaving(true);
      
      // Берем актуальные данные из глобального хранилища
      const actualPreText = testGlobalTexts['test-pre-message'] || preText;
      const actualTestText = testGlobalTexts['test-message'] || testText;
      const actualPositive = positiveResponses.map(item => testGlobalTexts[item.id] || item.text || '');
      const actualNegative = negativeResponses.map(item => testGlobalTexts[item.id] || item.text || '');
      
      const trimmedPositive = actualPositive.map(t => t.trim()).filter(t => t.length > 0);
      const trimmedNegative = actualNegative.map(t => t.trim()).filter(t => t.length > 0);

      await apiService.updateTestMessages({
        testPreMessage: {
          text: (actualPreText || '').trim(),
          orderIndex: 0
        },
        testMessage: {
          text: actualTestText.trim(),
          orderIndex: 1
        },
        testPositiveResponses: trimmedPositive.map((text, i) => ({
          text,
          orderIndex: i
        })),
        testNegativeResponses: trimmedNegative.map((text, i) => ({
          text,
          orderIndex: i
        }))
      });
      setError('');
    } catch (error) {
      setError('Ошибка при сохранении');
    } finally {
      setSaving(false);
    }
  }, [preText, testText, positiveResponses, negativeResponses]);

  const Wrapper: React.FC<{ children: React.ReactNode }> = ({ children }) => (
    embedded ? <div data-testid="welcome-test-section-test-true">{children}</div> : <div className="bg-white rounded-lg border border-[#e2e4e9] p-6">{children}</div>
  );

  return (
    <Wrapper>
      {!embedded && <h2 className="text-xl font-semibold text-[#000] mb-6">Тестовое сообщение для микрофона</h2>}

      {/* Инструкция перед тестовым сообщением */}
      <div className="space-y-4 mb-4">
        <PreMessageInput initialText={preText} />
      </div>

      {/* Тестовое сообщение */}
      <div className="space-y-4 mb-6">
        <TestMessageInput initialText={testText} />
      </div>

      {/* Позитивные ответы */}
      <div className="space-y-4 mb-6">
        <h3 className="text-lg font-medium text-[#000] mb-4">Позитивные ответы</h3>
        
        {positiveResponses.map((item, idx) => (
          <ResponseInput
            key={item.id}
            id={item.id}
            index={idx}
            initialText={item.text}
            type="positive"
            onRemove={handleRemovePositive}
          />
        ))}
      </div>

      {/* Негативные ответы */}
      <div className="space-y-4 mb-6">
        <h3 className="text-lg font-medium text-[#000] mb-4">Негативные ответы</h3>
        
        {negativeResponses.map((item, idx) => (
          <ResponseInput
            key={item.id}
            id={item.id}
            index={idx}
            initialText={item.text}
            type="negative"
            onRemove={handleRemoveNegative}
          />
        ))}
      </div>

      {error && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
          <p className="text-red-600 text-sm">{error}</p>
        </div>
      )}

      <div className="flex justify-between items-center">
        <div className="flex gap-3">
          <button
            onClick={handleAddPositive}
            className="bg-[#ffffff] box-border content-stretch flex flex-row gap-1 items-center justify-center overflow-clip p-[14px] relative rounded-2xl shrink-0 hover:bg-[#f6f8fa] transition-colors cursor-pointer h-[44px]"
          >
            <div className="box-border content-stretch flex flex-row gap-2 items-center justify-center px-1 py-0 relative shrink-0">
              <div className="font-['Inter:Medium',_sans-serif] font-medium leading-[0] not-italic relative shrink-0 text-[#525866] text-[16px] text-center text-nowrap tracking-[-0.176px]">
                <p className="adjustLetterSpacing block leading-[24px] whitespace-pre">
                  Добавить позитивный ответ
                </p>
              </div>
            </div>
          </button>
          <button
            onClick={handleAddNegative}
            className="bg-[#ffffff] box-border content-stretch flex flex-row gap-1 items-center justify-center overflow-clip p-[14px] relative rounded-2xl shrink-0 hover:bg-[#f6f8fa] transition-colors cursor-pointer h-[44px]"
          >
            <div className="box-border content-stretch flex flex-row gap-2 items-center justify-center px-1 py-0 relative shrink-0">
              <div className="font-['Inter:Medium',_sans-serif] font-medium leading-[0] not-italic relative shrink-0 text-[#525866] text-[16px] text-center text-nowrap tracking-[-0.176px]">
                <p className="adjustLetterSpacing block leading-[24px] whitespace-pre">
                  Добавить негативный ответ
                </p>
              </div>
            </div>
          </button>
          <button
            onClick={handleSave}
            className="px-6 py-2 text-white rounded-2xl transition-colors h-[44px]"
            style={{ backgroundColor: colors.primary }}
            onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = colors.primaryHover; }}
            onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = colors.primary; }}
            disabled={saving}
          >
            {saving ? 'Сохраняем...' : 'Сохранить'}
          </button>
        </div>
      </div>
    </Wrapper>
  );
};
